/**
 * Zustand Store: Release Pipeline Progress Manager
 *
 * Manages SSE subscriptions for the salesforce-release pipeline.
 * Unlike a React hook, this store persists across route changes so the
 * stream keeps running when the user navigates away.
 *
 * The subscription only ends when:
 * - Pipeline completes (completed / failed / cancelled terminal event)
 * - User explicitly calls stopStream()
 * - A new release is started IN THE SAME ORGANIZATION (old stream is replaced)
 *
 * Resilience (mirrors useRetrievalProgressInitial / useRetrievalProgressRefresh):
 * - Watchdog timer: if no SSE event arrives within WATCHDOG_TIMEOUT_MS the
 *   connection is silently dead — reconnect automatically.
 * - Reconnect on close: if the stream closes without a terminal event, wait
 *   RECONNECT_DELAY_MS then reopen. Keeps going until the job finishes.
 *
 * ORGANIZATION SCOPING
 * --------------------
 * A release pipeline runs against one organization's release document. The
 * store used to hold a single slot — one stream, one watchdog, one activity —
 * so starting a pipeline in Org B silently aborted Org A's, and Org A's progress
 * was visible from Org B until then.
 *
 * Each organization now gets its own slot: its own stream handle, its own
 * watchdog timer and its own reconnect logic. Handlers and timers close over the
 * organization they serve, so a reconnect fired long after a switch reopens the
 * right stream into the right slot. `state` publishes the active org's slot.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getQueryClient } from '@/dataProvider/reactQuery/context';
import { useActivityStore } from '@/stores/activityStore';
import {
  getActiveOrgId,
  isActiveOrg,
  readOrgSlice,
  registerOrgScopedState,
  writeOrgSlice,
  type OrgId,
} from '@/lib/org-scope';
import {
  streamPipeline,
  type PipelineEvent,
  type PipelineStage,
} from '@/services/salesforce-release';

// ============================================================================
// Types
// ============================================================================

interface StreamHandle {
  abort: () => void;
}

export interface ReleasePipelineState {
  releaseId: string | null;
  orgId: string | null;
  isActive: boolean;
  stage: PipelineStage | null;
  progress: number;
  elapsedSeconds: number;
  error: string | null;
  closed: boolean;
  activityId: string | null;
  activityStarted: boolean;
}

/** One organization's pipeline, plus the non-reactive machinery driving it. */
interface OrgPipelineSlot {
  state: ReleasePipelineState;
  streamHandle: StreamHandle | null;
  watchdogTimer: ReturnType<typeof setTimeout> | null;
}

interface ReleasePipelineStore {
  /** Per-organization pipelines. Not rendered. */
  byOrg: Map<string, OrgPipelineSlot>;

  /** The ACTIVE organization's pipeline. Read-only. */
  state: ReleasePipelineState;

  // Actions — operate on the organization that owns the release
  startStream: (releaseId: string, orgId: string) => void;
  /** Force-deactivate the UI without aborting the stream (used when doc status
   *  flips terminal faster than the SSE fires its terminal event). */
  forceDeactivate: (releaseId: string) => void;
  /** Stop the ACTIVE organization's stream and clear its slot. */
  stopStream: () => void;
  /** Alias of {@link stopStream}. Sign-out / explicit teardown, not org switch. */
  reset: () => void;

  // Internal actions — all addressed by the organization they belong to
  _slot: (orgId: OrgId | null) => OrgPipelineSlot;
  _writeSlot: (orgId: OrgId | null, slot: OrgPipelineSlot) => void;
  _patchState: (orgId: OrgId | null, updates: Partial<ReleasePipelineState>) => void;
  _armWatchdog: (orgId: OrgId | null) => void;
  _clearWatchdog: (orgId: OrgId | null) => void;
  _handleEvent: (orgId: OrgId | null, ev: PipelineEvent) => void;
  _handleError: (orgId: OrgId | null, err: unknown) => void;
  _handleClose: (orgId: OrgId | null) => void;
}

// ============================================================================
// Constants
// ============================================================================

const TERMINAL_PIPELINE_STAGES = new Set<string>(['completed', 'failed', 'cancelled']);
const WATCHDOG_TIMEOUT_MS = 30_000;
const RECONNECT_DELAY_MS = 2_000;

// ============================================================================
// Initial state
// ============================================================================

const initialState: ReleasePipelineState = {
  releaseId: null,
  orgId: null,
  isActive: false,
  stage: null,
  progress: 0,
  elapsedSeconds: 0,
  error: null,
  closed: false,
  activityId: null,
  activityStarted: false,
};

function emptySlot(): OrgPipelineSlot {
  return { state: initialState, streamHandle: null, watchdogTimer: null };
}

// ============================================================================
// Store
// ============================================================================

export const useReleasePipelineStore = create<ReleasePipelineStore>()(
  devtools(
    (set, get) => ({
      byOrg: new Map(),
      state: initialState,

      _slot: (orgId) => readOrgSlice(get().byOrg, orgId, emptySlot),

      _writeSlot: (orgId, slot) => {
        set((store) => {
          const byOrg = writeOrgSlice(store.byOrg, orgId, slot);
          return isActiveOrg(orgId) ? { byOrg, state: slot.state } : { byOrg };
        });
      },

      _patchState: (orgId, updates) => {
        const slot = get()._slot(orgId);
        get()._writeSlot(orgId, { ...slot, state: { ...slot.state, ...updates } });
      },

      // Arms (or re-arms) the inactivity watchdog for ONE organization.
      // If WATCHDOG_TIMEOUT_MS passes with no SSE event the connection is
      // silently dead — reconnect to wake it up.
      _armWatchdog: (orgId) => {
        const slot = get()._slot(orgId);
        if (slot.watchdogTimer) clearTimeout(slot.watchdogTimer);
        const timer = setTimeout(() => {
          const current = get();
          const { releaseId, orgId: slotOrgId, stage } = current._slot(orgId).state;
          if (releaseId && slotOrgId && !TERMINAL_PIPELINE_STAGES.has(stage ?? '')) {
            current.startStream(releaseId, slotOrgId);
          }
        }, WATCHDOG_TIMEOUT_MS);
        get()._writeSlot(orgId, { ...get()._slot(orgId), watchdogTimer: timer });
      },

      _clearWatchdog: (orgId) => {
        const slot = get()._slot(orgId);
        if (slot.watchdogTimer) {
          clearTimeout(slot.watchdogTimer);
          get()._writeSlot(orgId, { ...get()._slot(orgId), watchdogTimer: null });
        }
      },

      startStream: (releaseId, orgId) => {
        const slot = get()._slot(orgId);

        // Idempotent — already streaming for this exact release.
        if (
          slot.state.releaseId === releaseId &&
          slot.state.orgId === orgId &&
          slot.streamHandle !== null &&
          !slot.state.closed
        ) {
          return;
        }

        // Clear watchdog from any previous connection for this org.
        get()._clearWatchdog(orgId);

        // Close any existing connection for this org.
        if (slot.streamHandle) {
          slot.streamHandle.abort();
          // Only remove the activity when switching to a different release.
          // On reconnect (same release) we keep it so ActivityDropdown doesn't flicker.
          const prevActivityId = slot.state.activityId;
          if (prevActivityId && slot.state.activityStarted && slot.state.releaseId !== releaseId) {
            useActivityStore.getState().removeActivity(prevActivityId);
          }
        }

        const isSameRelease = slot.state.releaseId === releaseId;
        const activityId = `release_pipeline:${releaseId}`;

        get()._writeSlot(orgId, {
          streamHandle: null,
          watchdogTimer: null,
          state: {
            ...initialState,
            releaseId,
            orgId,
            activityId,
            // Preserve activityStarted on reconnect so the ActivityDropdown
            // doesn't reset to "Preparing…" between drop and first new event.
            activityStarted: isSameRelease ? slot.state.activityStarted : false,
          },
        });

        const handle = streamPipeline(
          releaseId,
          {
            onEvent: (ev) => get()._handleEvent(orgId, ev),
            onError: (err) => get()._handleError(orgId, err),
            onClose: () => get()._handleClose(orgId),
          },
          orgId
        );

        get()._writeSlot(orgId, { ...get()._slot(orgId), streamHandle: handle });

        // Arm the watchdog immediately — if the server never sends an event
        // within WATCHDOG_TIMEOUT_MS the connection is silently broken.
        get()._armWatchdog(orgId);
      },

      forceDeactivate: (releaseId) => {
        const orgId = getActiveOrgId();
        const slot = get()._slot(orgId);
        if (slot.state.releaseId !== releaseId) return;
        const activityId = slot.state.activityId;
        if (activityId) {
          useActivityStore.getState().removeActivity(activityId);
        }
        get()._patchState(orgId, { isActive: false, activityStarted: false });
      },

      stopStream: () => {
        const orgId = getActiveOrgId();
        get()._clearWatchdog(orgId);
        const slot = get()._slot(orgId);
        slot.streamHandle?.abort();
        const activityId = slot.state.activityId;
        if (activityId && slot.state.activityStarted) {
          useActivityStore.getState().removeActivity(activityId);
        }
        get()._writeSlot(orgId, emptySlot());
      },

      reset: () => get().stopStream(),

      _handleEvent: (orgId, ev) => {
        // Reset the inactivity watchdog on every received event.
        get()._armWatchdog(orgId);

        const store = get();
        const { releaseId, orgId: slotOrgId, activityId, activityStarted } = store._slot(orgId).state;
        if (!releaseId || !activityId) return;

        if (ev.event === 'state') {
          const isTerminal =
            TERMINAL_PIPELINE_STAGES.has(ev.stage) ||
            ev.status === 'failed' ||
            ev.status === 'cancelled' ||
            ev.status === 'completed';

          if (isTerminal) {
            // Terminal event — stop the watchdog, no more reconnects needed.
            store._clearWatchdog(orgId);
            store._patchState(orgId, { isActive: false, stage: ev.stage });

            if (ev.stage === 'completed' || ev.status === 'completed') {
              if (activityStarted) {
                useActivityStore.getState().completeActivity(activityId, 'Scan complete');
              }
              if (slotOrgId) {
                const qc = getQueryClient();
                qc.invalidateQueries({ queryKey: ['salesforce-release', 'documents', slotOrgId] });
                qc.invalidateQueries({ queryKey: ['salesforce-release', 'findings', slotOrgId, releaseId] });
              }
            } else if (ev.stage === 'failed' || ev.status === 'failed') {
              const msg = ev.error ?? 'Pipeline failed';
              store._patchState(orgId, { error: msg });
              if (activityStarted) {
                useActivityStore.getState().failActivity(activityId, msg);
              }
            } else {
              // cancelled
              if (activityStarted) {
                useActivityStore.getState().removeActivity(activityId);
              }
              if (slotOrgId) {
                getQueryClient().invalidateQueries({ queryKey: ['salesforce-release', 'documents', slotOrgId] });
              }
            }
            return;
          }

          // Active (non-terminal) state event — start activity on first one.
          if (!activityStarted) {
            useActivityStore.getState().startActivity(activityId, 'release_pipeline', 'Preparing…', { release_id: releaseId });
            store._patchState(orgId, { activityStarted: true });
          }

          store._patchState(orgId, { isActive: true, stage: ev.stage, progress: ev.progress });

          if (ev.stage === 'document_processing' && ev.status === 'running') {
            useActivityStore.getState().updateActivity(activityId, {
              progress: ev.progress,
              message: `Processing document · ${ev.progress}%`,
            });
          } else if (ev.stage === 'scan_queued') {
            useActivityStore.getState().updateActivity(activityId, { progress: ev.progress, message: 'Scan queued…' });
          } else if (ev.stage === 'scan_starting') {
            useActivityStore.getState().updateActivity(activityId, { progress: ev.progress, message: 'Scan starting…' });
          } else if (ev.stage === 'scan_running') {
            useActivityStore.getState().updateActivity(activityId, {
              progress: ev.progress,
              message: `Scanning · ${ev.progress}%`,
            });
          } else if (ev.stage === 'uploaded') {
            useActivityStore.getState().updateActivity(activityId, { progress: ev.progress, message: 'Preparing…' });
          }
        } else if (ev.event === 'queued') {
          store._patchState(orgId, { elapsedSeconds: ev.elapsed_s });
        } else if (ev.event === 'ingest_done') {
          if (activityStarted) {
            useActivityStore.getState().updateActivity(activityId, {
              progress: 50,
              message: `Document processed · ${ev.chunk_count} chunks`,
            });
          }
        }
      },

      _handleError: (orgId, err) => {
        // Errors are real failures — clear watchdog, set error state, do NOT reconnect.
        get()._clearWatchdog(orgId);
        const { activityId, activityStarted } = get()._slot(orgId).state;
        const msg = (err as Error)?.message ?? String(err);
        if (activityId && activityStarted) {
          useActivityStore.getState().failActivity(activityId, `Stream error: ${msg}`);
        }
        get()._patchState(orgId, { error: msg, isActive: false });
      },

      _handleClose: (orgId) => {
        get()._clearWatchdog(orgId);

        const { releaseId, orgId: slotOrgId, stage } = get()._slot(orgId).state;
        const isTerminal = TERMINAL_PIPELINE_STAGES.has(stage ?? '');

        get()._patchState(orgId, { closed: true });

        // Premature close (no terminal event received) — reconnect after a short delay.
        // Guards match the metadata hooks: re-check releaseId and terminal state after
        // the delay in case the user navigated away or another stream replaced this one.
        // The re-check reads THIS org's slot, so a switch in the meantime neither
        // cancels the reconnect nor points it at the wrong organization.
        if (!isTerminal && releaseId && slotOrgId) {
          setTimeout(() => {
            const current = get();
            const slotState = current._slot(orgId).state;
            if (
              slotState.releaseId === releaseId &&
              !TERMINAL_PIPELINE_STAGES.has(slotState.stage ?? '')
            ) {
              current.startStream(releaseId, slotOrgId);
            }
          }, RECONNECT_DELAY_MS);
        }
      },
    }),
    { name: 'release-pipeline-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/** Re-point `state` at the newly active organization's pipeline. Nothing is stopped. */
registerOrgScopedState({
  name: 'releasePipelineStore',
  onOrgChange: (nextOrgId) => {
    useReleasePipelineStore.setState((store) => ({
      state: readOrgSlice(store.byOrg, nextOrgId, emptySlot).state,
    }));
  },
  onSignOut: shutdownAllReleasePipelines,
});

/** Abort every organization's pipeline stream and timer. Sign-out only. */
export function shutdownAllReleasePipelines(): void {
  const { byOrg } = useReleasePipelineStore.getState();
  for (const slot of byOrg.values()) {
    slot.streamHandle?.abort();
    if (slot.watchdogTimer) clearTimeout(slot.watchdogTimer);
  }
  useReleasePipelineStore.setState({ byOrg: new Map(), state: initialState });
}

// ============================================================================
// Selectors
// ============================================================================

export const selectReleasePipelineState = (store: ReleasePipelineStore) => store.state;
export const selectIsActive = (store: ReleasePipelineStore) => store.state.isActive;
export const selectPipelineStage = (store: ReleasePipelineStore) => store.state.stage;
